import { A11yModule } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatToolbarModule } from '@angular/material/toolbar';

import { Store } from '@ngrx/store';

import { Observable, of } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';

import { QuotesActions } from '../../../@store/quotes/quotes.actions';
import {
  selectItemProcesses,
  selectItemProcessesCategories,
  selectItemProcessesError,
  selectItemProcessesLoading,
  selectSelectedItemProcess,
} from '../../../@store/quotes/quotes.selectors';
import { SafeAny } from '../../../types/safe-any';

interface ItemProcess {
  id: string | number;
  description: string;
  itemId: string;
  [key: string]: unknown;
}

@Component({
  selector: 'ciwc-add-quote-details',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatToolbarModule,
    MatButtonModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    A11yModule,
  ],
  templateUrl: './add-item-process-to-quote-dialog.component.html',
  styleUrls: ['./add-item-process-to-quote-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddItemProcessToQuoteDialogComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);
  private readonly fb = inject(FormBuilder);
  readonly itemProcesses = this.store.selectSignal(selectItemProcesses);
  readonly itemProcessesLoading = this.store.selectSignal(selectItemProcessesLoading);
  readonly itemProcessesError = this.store.selectSignal(selectItemProcessesError);
  readonly selectedItemProcess = this.store.selectSignal(selectSelectedItemProcess);
  readonly itemProcessesCategories = this.store.selectSignal(selectItemProcessesCategories);

  quoteDetailsForm: FormGroup;
  filteredOptions$: Observable<ItemProcess[]> = of([]);

  constructor(private dialogRef: MatDialogRef<AddItemProcessToQuoteDialogComponent>) {
    this.quoteDetailsForm = this.fb.group({
      itemType: ['', Validators.required],
      uniqueId: [''],
      itemId: [''],
      description: ['', Validators.required],
    });

    effect(() => {
      const selectedProcess = this.selectedItemProcess();

      if (selectedProcess) {
        this.populateFormWithSelectedProcess(selectedProcess);
      }
    });
  }

  ngOnInit(): void {
    this.store.dispatch(
      QuotesActions.loadItemProcesses({ payload: { limit: -1, sort: 'itemId' } }),
    );

    this.setupItemProcessFilter();
    this.setupCategoryListener();
  }

  displayFn = (item: ItemProcess | null): string => {
    return item ? item['itemId'] : '';
  };

  onCancel(): void {
    this.store.dispatch(QuotesActions.clearItemProcessState());
    this.dialogRef.close();
  }

  onSubmit(): void {
    const quoteDetails = this.quoteDetailsForm.value;

    if (quoteDetails.uniqueId) {
      quoteDetails.uniqueId = quoteDetails.uniqueId.id;
    }

    this.store.dispatch(QuotesActions.clearItemProcessState());
    this.dialogRef.close(this.quoteDetailsForm.value);
  }

  private setupItemProcessFilter(): void {
    const control = this.quoteDetailsForm.get('uniqueId');

    if (control) {
      this.filteredOptions$ = control.valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith(''),
        map((value) => {
          if (!value || typeof value !== 'string') {
            return this.itemProcesses() || [];
          }
          const filterValue = value.toLowerCase();
          return (this.itemProcesses() || []).filter((item) => {
            return item.itemId.toLowerCase().includes(filterValue);
          });
        }),
      );

      control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
        if (value && typeof value === 'object' && value.id) {
          this.store.dispatch(QuotesActions.loadSpecificItemProcess({ id: value.id }));
        }
      });
    }
  }

  private setupCategoryListener(): void {
    const categoryControl = this.quoteDetailsForm.get('itemType');

    if (categoryControl) {
      categoryControl.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
        .subscribe((itemType) => {
          if (!itemType) return;

          if (this.selectedItemProcess()?.itemType !== itemType) {
            this.quoteDetailsForm.get('uniqueId')?.setValue('');
          }

          this.store.dispatch(
            QuotesActions.loadItemProcesses({ payload: { category: itemType, sort: 'itemId' } }),
          );
          this.updateFormControls(itemType);
        });
    }
  }

  private updateFormControls(itemType: string): void {
    // Remove all existing controls except itemType, uniqueId, and itemId
    const controlsToKeep = ['itemType', 'uniqueId', 'itemId'];

    Object.keys(this.quoteDetailsForm.controls).forEach((control) => {
      if (!controlsToKeep.includes(control)) {
        this.quoteDetailsForm.removeControl(control);
      }
    });

    // Add base controls
    this.quoteDetailsForm.addControl('description', this.fb.control('', Validators.required));

    // Add itemType-specific controls
    switch (itemType) {
      case 'OTHER':
        this.quoteDetailsForm.addControl('pricePerUnit', this.fb.control('', Validators.required));
        break;
      case 'COPPER':
        this.quoteDetailsForm.addControl('lbPer1000ft', this.fb.control('', Validators.required));
        this.quoteDetailsForm.addControl('pricePerLb', this.fb.control('', Validators.required));
        this.quoteDetailsForm.addControl(
          'basePrice',
          this.fb.control({
            value: this.itemProcessesCategories().find((item: SafeAny) => item.key === 'COPPER')
              .basePrice,
            disabled: true,
          }),
        );
        break;
      case 'SINGLE_END':
        this.quoteDetailsForm.addControl(
          'specificGravity',
          this.fb.control('', Validators.required),
        );
        this.quoteDetailsForm.addControl('constant1', this.fb.control('', Validators.required));
        this.quoteDetailsForm.addControl('pricePerLb', this.fb.control('', Validators.required));
        this.quoteDetailsForm.addControl(
          'basePrice',
          this.fb.control({
            value: this.itemProcessesCategories().find((item: SafeAny) => item.key === 'SINGLE_END')
              .basePrice,
            disabled: true,
          }),
        );
        break;
      case 'COMPOUNDS':
      case 'TEFLON_TAPES':
        this.quoteDetailsForm.addControl(
          'specificGravity',
          this.fb.control('', Validators.required),
        );
        this.quoteDetailsForm.addControl('pricePerLb', this.fb.control('', Validators.required));
        break;
      case 'ALL_TAPES_AND_FILLERS':
        this.quoteDetailsForm.addControl(
          'specificGravity',
          this.fb.control('', Validators.required),
        );
        this.quoteDetailsForm.addControl('constant1', this.fb.control('', Validators.required));
        this.quoteDetailsForm.addControl('constant2', this.fb.control('', Validators.required));
        this.quoteDetailsForm.addControl('pricePerLb', this.fb.control('', Validators.required));
        break;
    }
  }

  private populateFormWithSelectedProcess(
    selectedProcess: ItemProcess & {
      itemType: string;
      description: string;
      basePricePerUnit?: number;
      lbPer1000ft?: number;
      basePrice?: number;
      specificGravity?: number;
      constant1?: number;
      constant2?: number;
      basePricePerLb?: number;
      pricePerLb?: number;
      pricePerUnit?: number;
    },
  ): void {
    // Set the itemType based on itemType
    this.quoteDetailsForm.patchValue({
      itemType: selectedProcess.itemType,
      itemId: selectedProcess['itemId'],
      description: selectedProcess.description,
    });

    // Handle itemType-specific fields
    switch (selectedProcess.itemType) {
      case 'OTHER':
        this.quoteDetailsForm.patchValue({
          pricePerUnit: selectedProcess.pricePerUnit,
        });
        break;
      case 'COPPER':
        this.quoteDetailsForm.patchValue({
          lbPer1000ft: selectedProcess.lbPer1000ft,
          pricePerLb: selectedProcess.pricePerLb,
        });
        break;
      case 'SINGLE_END':
        this.quoteDetailsForm.patchValue({
          specificGravity: selectedProcess.specificGravity,
          constant1: selectedProcess.constant1,
          pricePerLb: selectedProcess.pricePerLb,
        });
        break;
      case 'COMPOUNDS':
      case 'TEFLON_TAPES':
        this.quoteDetailsForm.patchValue({
          specificGravity: selectedProcess.specificGravity,
          pricePerLb: selectedProcess.pricePerLb,
        });
        break;
      case 'ALL_TAPES_AND_FILLERS':
        this.quoteDetailsForm.patchValue({
          specificGravity: selectedProcess.specificGravity,
          constant1: selectedProcess.constant1,
          constant2: selectedProcess.constant2,
          pricePerLb: selectedProcess.pricePerLb,
        });
        break;
    }
  }
}
